import React from 'react';

// Shared derived-data helpers for SaleReportDetail's tabs — extracted verbatim from the
// pre-split SaleReportDetail.js (phase 2 UI cleanup) so every tab reads the same logic
// instead of risking copy-pasted copies drifting apart.

export const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

// itemsBreakdown's iceCode carries whatever price was embedded at the time of that sale
// (e.g. "1I35B", "1I40B" — same base product, different historical price). The seller's
// DefaultPrice — what forgot-sale prices against — only ever keys by the BASE code ("1I").
// Null if the code doesn't even start with the digit+I shape.
export const extractBaseIceCode = (iceCode) => {
    const m = /^(\d+)[Ii]/.exec(iceCode || '');
    return m ? `${m[1]}I` : null;
};

export const getSection = (obj, keys, def) => {
    for (const k of keys) {
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return def;
};

// Normalize Items and Customers data to handle wrapped shapes
export const getItemsData = (report) => {
    if (!report) return { items: [], totalQuantity: 0, totalPrice: 0 };
    // Accept both itemsBreakdown: { items, totalQuantity, totalPrice } and flat arrays
    const ib = report.itemsBreakdown || report.itemBreakdown || null;
    if (ib && (Array.isArray(ib.items) || Array.isArray(ib.itemList))) {
        const items = ib.items || ib.itemList || [];
        const totalQuantity = ib.totalQuantity ?? 0;
        const totalPrice = ib.totalPrice ?? 0;
        return { items, totalQuantity, totalPrice };
    }
    // Fallback to older shapes
    const arr = getSection(report, ['items', 'itemList'], []);
    const items = asArray(arr);
    const totalQuantity = items.reduce((s, x) => s + (Number(x?.quantity) || 0), 0);
    const totalPrice = items.reduce((s, x) => s + (Number(x?.total) || 0), 0);
    return { items, totalQuantity, totalPrice };
};

export const getBagSalesData = (report) => {
    if (!report) return { items: [], totalQuantity: 0, totalPrice: 0 };
    const bsb = report.bagSalesBreakdown || null;
    if (bsb && Array.isArray(bsb.items)) {
        // Normalize items to ensure consistency between productName/bagName
        const normalizedItems = bsb.items.map(it => ({
            ...it,
            displayProductName: it.bagName || it.productName || 'ไม่ระบุ'
        }));
        return {
            items: normalizedItems,
            totalQuantity: bsb.totalQuantity ?? 0,
            totalPrice: bsb.totalPrice ?? 0
        };
    }
    return { items: [], totalQuantity: 0, totalPrice: 0 };
};

export const formatDateTime = (dtStr) => {
    if (!dtStr) return '';
    try {
        const date = new Date(dtStr);
        if (isNaN(date.getTime())) return dtStr;
        return date.toLocaleString('th-TH', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch (e) {
        return dtStr;
    }
};

export const getCustomersData = (report) => {
    if (!report) return { list: [], count: 0 };
    // customers may be { count, customers: [] }
    const c = report.customers || report.customerList || null;
    if (c && Array.isArray(c.customers)) {
        return { list: c.customers, count: Number(c.count) || c.customers.length };
    }
    const list = asArray(c);
    return { list, count: list.length };
};

// Group items by product name and unit price
export const groupItemsByNameAndPrice = (items) => {
    const groupedMap = new Map();
    const toNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };
    for (const it of items || []) {
        const name = (it?.productName ?? it?.name ?? it?.iceName ?? 'ไม่ระบุ').toString();
        const unitPrice = toNum(it?.unitPrice ?? it?.price);
        const iceCode = (it?.iceCode ?? '').toString();
        const key = `${name}__${unitPrice}`;
        const exist = groupedMap.get(key) || { productName: name, iceCode, unitPrice, quantity: 0, total: 0 };
        const qty = toNum(it?.quantity);
        const lineTotal = toNum(it?.total);
        exist.quantity += qty;
        // Prefer provided total; otherwise compute
        exist.total += lineTotal > 0 ? lineTotal : (unitPrice * qty);
        groupedMap.set(key, exist);
    }
    return Array.from(groupedMap.values());
};

// Vector 2026-09-25 11:20 ICT: "ส่วน ยอดขายรวม (Sales Summary) ให้มี summary รวมยอด สินค้าแต่ละชนิด"
// Vector 2026-09-25 12:48 ICT: "1I หลอด ต้องมาก่อน 2I บด เสมอ ... หลอด ราคาต่ำสุด ลงไปหาเยอะสุด
// สลับ หลอดบด ตามระดับราคา" — price rows run cheapest → dearest, and within one price หลอด (1I)
// comes before บด (2I). Ordered by iceCode, not the Thai name (localeCompare puts บด before หลอด).
// One subtotal row per product follows, in the same product order.
const productOrder = (a, b) => (
    (a.iceCode || a.productName).localeCompare(b.iceCode || b.productName, 'th')
);

export const buildSalesSummaryRows = (items) => {
    const grouped = groupItemsByNameAndPrice(items);
    const sorted = [...grouped].sort((a, b) => (
        a.unitPrice === b.unitPrice ? productOrder(a, b) : a.unitPrice - b.unitPrice
    ));
    const subtotalMap = new Map();
    for (const r of sorted) {
        const sub = subtotalMap.get(r.productName)
            || { type: 'subtotal', productName: r.productName, iceCode: r.iceCode, quantity: 0, total: 0 };
        sub.quantity += r.quantity;
        sub.total += r.total;
        subtotalMap.set(r.productName, sub);
    }
    return [
        ...sorted.map((r) => ({ type: 'item', ...r })),
        ...Array.from(subtotalMap.values()).sort(productOrder),
    ];
};

// Vector 2026-09-25 13:41: Sales Summary shows each product by its product-master "รายละเอียด"
// (1I → น้ำแข็งหลอดใหญ่, 2I → น้ำแข็งบด). Sale lines carry an iceCode like "1I35B", so match the
// longest productCode that prefixes it; no match (or master not loaded) keeps the sale's own name.
export const productLabel = (iceCode, fallbackName, productMaster) => {
    const code = (iceCode || '').toString();
    let best = null;
    for (const p of productMaster || []) {
        const pc = (p?.productCode || '').toString();
        if (pc && code.startsWith(pc) && (!best || pc.length > best.productCode.length)) best = p;
    }
    return (best && best.description) || fallbackName;
};

export const renderTable = (title, items, cols, emptyText = 'ไม่มีข้อมูล', footer = null, note = null) => (
    <div className="btc-section">
        <h3>{title}</h3>
        {items && items.length > 0 ? (
            <table className="btc-table">
                <thead>
                    <tr>
                        {cols.map((c) => (
                            <th key={c.key}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {items.map((it, idx) => (
                        <tr key={idx}>
                            {cols.map((c) => (
                                <td key={c.key}>{c.format ? c.format(it[c.key], it) : (it[c.key] ?? '')}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                {footer && (
                    <tfoot style={{ backgroundColor: '#b1becc' }}>
                        {footer}
                    </tfoot>
                )}
            </table>
        ) : (
            <p>{emptyText}</p>
        )}
        {note && <p style={{ color: '#6c757d', fontSize: '13px' }}>{note}</p>}
    </div>
);
